import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import clone from 'lodash/clone';
import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import { useStore } from 'hooks';
import { IObject } from 'types';
import { FiasSearchProps } from '../../types';
import { ReactComponent as crossIcon } from 'assets/icons/cross.svg';
import { SVG } from 'components';
import { SelectedItem } from './components';
import { Wrapper, SelectedItemWrapper } from './selected-items.styled';

type SelectedItemProps = Pick<
  FiasSearchProps,
  'flat' | 'withSectors' | 'isDisabled' | 'disabledHouses'
>;

export const SelectedItems: FC<SelectedItemProps> = observer(
  ({ flat, withSectors, isDisabled, disabledHouses }) => {
    const { fias } = useStore();
    const [currentSelectedItems, setCurrentSelectedItems] = useState<IObject[]>(
      fias.confirmedSelectedHouses
    );

    useEffect(() => {
      if (withSectors && !fias.selectedSectors.length) {
        fias.clearFiasSearch();
      }
    }, [fias.selectedSectors.length, withSectors]);

    // Преобразование данных в нужный вид
    useEffect(() => {
      let params: IObject[] = [];
      const housesParams: IObject[] = clone(fias.confirmedSelectedHouses);

      if (flat) {
        // Сопоставление выбранных домов и выбранных квартир в домах
        const flatParams: IObject[] = clone(fias.confirmedSelectedFlats);

        for (let i = 0; i < housesParams.length; i++) {
          for (let j = 0; j < flatParams.length; j++) {
            if (housesParams[i]._id === flatParams[j]._id) {
              params.push(flatParams[j]);
            }
          }

          if (
            !params.filter(
              (item: IObject) => item._id && item._id === housesParams[i]._id
            ).length
          ) {
            params.push(housesParams[i]);
          }
        }
      } else {
        params = housesParams;
      }

      // Разделение домов и остальных выбранных объектов
      let notLastLvlParams: IObject[] = [];
      let lastLvlParams: IObject[] = params.filter((item: IObject): boolean => {
        if (!item.houses && !item.inheritors) {
          return true;
        }
        notLastLvlParams.push(item);

        return false;
      });

      if (withSectors) {
        lastLvlParams = lastLvlParams.filter(
          (item) =>
            !!intersection(fias.selectedSectors, item.sectors).length &&
            fias.selectedSectors.length <=
              intersection(fias.selectedSectors, item.sectors).length
        );

        notLastLvlParams = notLastLvlParams.filter(
          (item) =>
            !!intersection(fias.selectedSectors, item.sectors).length &&
            fias.selectedSectors.length <=
              intersection(fias.selectedSectors, item.sectors).length
        );
      }

      // Вкладывание домов в улицу в случае их наличия среди выбранных
      if (!!lastLvlParams.length) {
        for (let i = 0; i < lastLvlParams.length; i++) {
          const result: IObject | null = searchStreet(
            withSectors ? fias.treeWithSectorsFiltered : fias.tree,
            lastLvlParams[i]
          );
          const existingItem: IObject | undefined = notLastLvlParams.find(
            (item: IObject) =>
              item.AOGUID && result !== null && item.AOGUID === result.AOGUID
          );

          // Замена существующего объекта на объект со вложенными домами
          if (existingItem) {
            const newItem: IObject = { ...existingItem };
            newItem.selectedHouses = newItem.selectedHouses || [];
            newItem.selectedHouses.push(lastLvlParams[i]);
            notLastLvlParams = notLastLvlParams.map(
              (item: IObject): IObject => {
                if (item.AOGUID && item.AOGUID === newItem.AOGUID) {
                  return newItem;
                }

                return item;
              }
            );
            // Добавление объекта со вложенными домами в случае его отсутствия
          } else {
            const newItem: IObject = { ...result };
            newItem.selectedHouses = [lastLvlParams[i]];
            notLastLvlParams.push(newItem);
          }
        }
        //notLastLvlParams = concatFiases(notLastLvlParams);
        params = notLastLvlParams;
      }

      // Установка в state массива с необходимыми данными для показа пользователю выбранных объектов
      setCurrentSelectedItems(params);

      // Объединение выходных данных для дальнейшего использования вне компонента, а также удаление улицы в случае, если внутри выбраны дома
      const selectedGroups: IObject[] = [];
      const selectedFias: IObject[] = [];
      const selectedHouses: IObject[] = [];

      params.forEach((item: IObject): void => {
        if (!item.selectedHouses) {
          item.id && selectedGroups.push(item);
          item.AOGUID && selectedFias.push(item);
          item._id && selectedHouses.push(item);
        } else {
          if (
            item.AOGUID &&
            item.houses.length &&
            item.selectedHouses.length === item.houses.length
          ) {
            selectedFias.push(item);
          } else {
            item.selectedHouses.forEach((item: IObject) =>
              selectedHouses.push(item)
            );
          }
        }
      });

      //selectedFias = concatFiases(selectedFias);

      fias.setSelectedGroups(selectedGroups);
      fias.setSelectedFias(selectedFias);
      fias.setSelectedHouses(selectedHouses);
    }, [
      fias.confirmedSelectedHouses.length,
      fias.confirmedSelectedFlats.length,
      fias.selectedSectors.length,
    ]);

    /* Чтобы "Парголово" на разбивалось на кучу улиц */
    // 1. Найти родителя по id.
    // 2. Понять что кол-во Inheritors родителя равно groups[key].length.
    // 3. Удостовериться что число houses равно числу selectedHouses у каждого элемента.
    // 4. Запушить родителя если условия прошли, если нет - запушить каждый элемент отдельно
    // const concatFiases = (entryData: IObject[]): IObject[] => {
    //TODO: сейчас не используется, но может пригодиться при переработке компонента.
    // Данное решение пока оказалось не совершенным, т.к. оно не только не разбивало Парголово на улицы (как и нужно),
    // но и собирало отдельные улицы в единое Парголово (как не нужно)
    //const formattedSelectedFias: IObject[] = [];
    //
    //   if (entryData.length) {
    //     const groups = groupBy(entryData, 'parent'); groupBy from lodash
    //
    //     for (const key in groups) {
    //       if (groups[key].length > 1) {
    //         const parent = fias.getFiasParent(key);
    //
    //         if (!parent?.AOGUID) {
    //           console.log('PARENT NOT FOUND');
    //         }
    //
    //         if (parent.inheritors?.length === groups[key].length) {
    //           const allHousesSelected = groups[key].filter(
    //             (item) => item.houses.length === item.selectedHouses.length
    //           );
    //
    //           if (allHousesSelected.length === groups[key].length) {
    //             formattedSelectedFias.push(parent);
    //           } else {
    //             formattedSelectedFias.push(...groups[key]);
    //           }
    //         } else {
    //           formattedSelectedFias.push(...groups[key]);
    //         }
    //       } else {
    //         formattedSelectedFias.push(groups[key][0]);
    //       }
    //     }
    //   }
    //
    //   return formattedSelectedFias;
    // };

    // Поиск улицы, на которой находится дом
    const searchStreet = (items: IObject[], house: IObject): IObject | null => {
      let result = null;

      items.some((item: IObject): boolean => {
        if (item.AOGUID === house.parent) {
          result = item;

          return true;
        }

        if (item.inheritors) {
          result = searchStreet(item.inheritors, house);

          return result !== null;
        }

        return false;
      });

      return result;
    };

    const findSelectedItem = (items: IObject[], id: string): IObject[] => {
      return items.filter((item: IObject) => {
        if (id !== (item.AOGUID || item.id || item._id)) {
          return true;
        }
        fias.setShowSecondOptionsField(false);

        return false;
      });
    };

    const findSelectedHouses = (
      items: IObject[],
      searchedItem: IObject,
      index: number
    ): IObject[] => {
      return items.filter((house: IObject): boolean => {
        if (searchedItem.selectedHouses[index]._id !== house._id) {
          return true;
        }
        fias.setShowSecondOptionsField(false);

        return false;
      });
    };

    const deleteOption = (item: IObject): void => {
      let params: IObject[] = clone(fias.confirmedSelectedHouses);
      const currentId: string = item.AOGUID || item.id || item._id;
      params = findSelectedItem(params, currentId);

      if (item.selectedHouses && item.selectedHouses.length) {
        for (let i = 0; i < item.selectedHouses.length; i++) {
          params = findSelectedHouses(params, item, i);
        }
      }

      if (flat) {
        let flatParams: IObject[] = clone(fias.confirmedSelectedFlats);
        flatParams = findSelectedItem(flatParams, currentId);

        if (item.selectedHouses && item.selectedHouses.length) {
          for (let i = 0; i < item.selectedHouses.length; i++) {
            flatParams = findSelectedHouses(flatParams, item, i);
          }
        }
        fias.setConfirmedSelectedFlats(flatParams);
        fias.setCurrentSelectedFlats(flatParams);
      }

      //TODO: Был баг при использовании дерева с секторами:
      // Выбираем улицу у которой 30 домов.
      // Выбираем направление под которое попадают только 4 дома.
      // Удаляем адрес.
      // В итоге пустыми "крестиками" заполнялось поле выбранного под FiasSearch
      // и заполнялось теми домами, которые под фильтр не попадали.
      // Аналогично если с той же выбранной улицей (30 домов) выбрать направление под которое попадает 29 домов,
      // то после удаления адреса под селектом появится один крестик (1 дом который не удалился так как не был в офильтрованном списке)
      if (fias.selectedSectors.length) {
        params = params.filter(
          (item) =>
            !!intersection(fias.selectedSectors, item.sectors).length &&
            fias.selectedSectors.length <=
              intersection(fias.selectedSectors, item.sectors).length
        );
      }

      fias.setConfirmedSelectedHouses(params);
      fias.setCurrentSelectedHouses(params);
    };

    return (
      <>
        {!!currentSelectedItems.length && (
          <Wrapper isDisabled={isDisabled}>
            {currentSelectedItems?.map((item: IObject, index: number) => (
              <SelectedItemWrapper key={index}>
                <SelectedItem item={item} disabledHouses={disabledHouses} />
                <SVG
                  src={crossIcon}
                  props={{
                    color: '#697180',
                    hoverColor: '#2D67D8',
                    cursor: 'pointer',
                  }}
                  action={() => deleteOption(item)}
                />
              </SelectedItemWrapper>
            ))}
          </Wrapper>
        )}
      </>
    );
  }
);
